import { Color } from 'three';

/**
 * ART DIRECTION — locked to the "Heist Game / Visual Directions" reference sheet.
 *
 * The whole look rests on four rules, and every value below serves one of them:
 *
 *  1. NIGHT IS BLUE-GREY, NOT BLACK. Shadows lift toward slate. Pure black never
 *     appears on screen except inside doorways we want to read as voids.
 *  2. LIGHT IS POOLED, NOT AMBIENT. Fixtures make discrete puddles on the floor
 *     with hard-ish falloff. The space between pools is where thieves live.
 *  3. COLOUR ONLY FROM EMITTERS. Concrete, tile, plaster and mannequins are all
 *     desaturated. Every saturated pixel is a sign, a screen, or a potion bottle.
 *  4. SCALE READS THROUGH FOG. Depth haze is what makes the atrium feel huge and
 *     the corridors feel endless. Fog density is a level-design tool, not a preset.
 */

const hex = (h: number) => new Color().setHex(h, 'srgb');

/** Surface albedos. Deliberately narrow value range — the lighting does the work. */
export const SURFACE = {
  /** Poured concrete: floors of service corridors, parking structure, walls. */
  concrete: hex(0x6b6f75),
  concreteDark: hex(0x4a4e54),
  /** Polished mall tile — the atrium and shopfront walkways. Slight warm lift. */
  tileLight: hex(0x9a978f),
  tileDark: hex(0x6e6b66),
  /** Backrooms checkerboard (panel 4): mustard + cream, heavily desaturated. */
  checkerWarm: hex(0x8d7a4e),
  checkerPale: hex(0xb3a887),
  /** Painted plaster storefront bulkheads. */
  plaster: hex(0x8f8b84),
  plasterDim: hex(0x63605b),
  /** Near-black display cloth. See MATERIALS.velvet. */
  velvet: hex(0x191512),
  /** Kraft cardboard — stock boxes, back-of-house clutter, shelf packaging. */
  cardboard: hex(0x6d5a41),
  /** Structural steel, escalator sides, railings. */
  steel: hex(0x555a61),
  steelDark: hex(0x3a3e44),
  /** Glass — shopfronts and the atrium canopy. Very dark, mostly reflective. */
  glass: hex(0x1a2029),
  /** Mannequins (panels 1, 4, 5, 7): pale clay, never skin-coloured. */
  mannequin: hex(0xb9ad9a),
  /** Rubber, tyres, bin liners, cable runs. */
  rubber: hex(0x24262a),
  /**
   * Tarmac. Its own entry rather than borrowing `rubber`, because the forecourt
   * is the one surface in the game lit ONLY by baked irradiance — a twelve-metre
   * south wall puts the whole apron in the moon's shadow — and baked light is
   * added as `irradiance × albedo`. At rubber's 0.018 linear that multiply threw
   * away 98% of every photon the lamps spent, and the car park measured 9/255
   * with the columns at full power. Tarmac is genuinely dark, but it is not
   * rubber-dark, and out here the difference is the whole image.
   */
  tarmac: hex(0x33363c),
  /** Timber — food court tables, planter boxes. */
  timber: hex(0x6a563f),
  /** Fake foliage (panel 5) — dusty, unhealthy green. */
  foliage: hex(0x3f5340),
  /**
   * Guard uniform. Almost black under moonlight, which is the point — a guard
   * is meant to be a silhouette you resolve too late, and the only part of one
   * you reliably see at distance is the retroreflective banding.
   */
  uniform: hex(0x2b323c),
  uniformDark: hex(0x1d222a),
  /** Uniform trim and cap band. */
  uniformTrim: hex(0x424b58),
  /**
   * Thief kit. Deliberately warm-dark where the guard uniform is blue-dark: at
   * the distance this game is played at you will not resolve a face, and the
   * one-frame judgement "is that one of mine" has to come off the hue of a
   * silhouette. Everything else in the building is neutral grey, so the two
   * teams are the only warm and cool darks in the frame.
   */
  thiefCoat: hex(0x35302a),
  thiefCoatDark: hex(0x24201a),
  thiefTrouser: hex(0x2d2a26),
  /** Webbing, straps and the pack. A shade up, so the load reads in silhouette. */
  thiefStrap: hex(0x4b4438),
} as const;

/** Emissive colours. These are the ONLY saturated things in the frame. */
export const EMIT = {
  /**
   * Hi-vis banding on a guard's vest, and the lens of their torch.
   *
   * Emissive rather than a plain surface because retroreflective tape genuinely
   * is: it throws almost all the light that hits it straight back at whoever is
   * looking. In a building this dark it is the single readable thing about a
   * guard at thirty metres, so it has to be a light source, not a colour.
   */
  hiVis: hex(0xcadb4e),
  torch: hex(0xfff2d8),
  /** Cold fluorescent tube — mall ceiling strips, control room. */
  fluoro: hex(0xcfe4ff),
  /** Warm interior spill from a lit shop unit. */
  shopWarm: hex(0xffc98a),
  /** Sodium vapour — parking structure only (panel 7). */
  sodium: hex(0xff9a4a),
  /** Amber shop signage (DEPARTMENT OF THINGS, ETERNAL YOU). */
  signAmber: hex(0xffb347),
  /** Cyan/clinical corporate signage (Aperture-alike, panel 2). */
  signCyan: hex(0x7fd4e8),
  /** Magenta — the weird stores. Sparingly. */
  signMagenta: hex(0xd96bb8),
  /** Potion Emporium bottle glow (panel 6). */
  potionA: hex(0xc23fd4),
  potionB: hex(0x3fd48f),
  potionC: hex(0xd4923f),
  /** CRT security monitors (panel 9). */
  crt: hex(0x86c7a8),
  /** Green EXIT signage. */
  exitGreen: hex(0x4bd08a),
  /** Walkie-talkie LCD. */
  lcd: hex(0x9fe0b0),
  /**
   * Moonlight on a surface. Not a fitting — this is the colour a wash uses when
   * the thing it is painting should read as lit by the sky rather than by a lamp.
   * The plaza façade faces south and the moon sits north-west, so without this
   * the only light reaching it is sodium and the whole front of the building
   * goes warm tan — the one colour the reference sheet never uses.
   */
  moonlit: hex(0xa9c2e0),
} as const;

/** Atmosphere. Fog colour is the single strongest lever on the whole look. */
export const ATMOS = {
  /** Night sky / infinite distance. Matches fog so geometry dissolves cleanly. */
  skyNight: hex(0x0b1119),
  /** Interior haze — cool slate. */
  fogInterior: hex(0x141c26),
  /** Exterior haze under moonlight — lifts slightly bluer and brighter. */
  fogExterior: hex(0x1b2634),
  /** Backrooms/service haze — warm, sickly, from fluorescent bounce (panel 4). */
  fogService: hex(0x241f16),
  /** Fog densities (FogExp2). Tuned so a 40m corridor fully dissolves. */
  density: {
    atrium: 0.014,
    corridor: 0.028,
    service: 0.045,
    exterior: 0.011,
    parking: 0.022,
  },
} as const;

/** Light rig values. Intensities assume physically-correct lighting + AgX tonemap. */
export const LIGHTS = {
  moon: {
    color: hex(0xaec6e8),
    intensity: 0.95,
    /** Direction the moon sits, relative to world origin. High and to the north-west. */
    /**
     * North-west and high. The mall runs north (-Z), so this puts the moon at
     * the far end of the atrium — visible through the canopy when you look down
     * the length of the building, which is the shot reference panel 5 is built
     * around. `moonUVFromDirection` paints the sky from this vector, so moving
     * it moves the moon and the shadows together.
     */
    /**
     * Elevation matters as much as bearing. High overhead and the moon sits
     * above the top of frame in every eye-level shot and only ever appears as a
     * shadow; at ~33° it reads through the canopy glazing when you look down the
     * atrium, and the shadows it throws are long and raking instead of flat.
     */
    position: [-22, 52, -78] as const,
  },
  /**
   * Sky/ground bounce. Low enough that rule 2 (pooled light) still holds, but
   * high enough that concrete reads as slate rather than black — the reference
   * sheet has no crushed shadows anywhere, and neither should we.
   *
   * Deliberately weak. Hemisphere light reaches everywhere including sealed
   * interiors, so any value high enough to carry a room on its own also paints
   * the back-of-house corridors sky-blue and flattens the pooled-light rule.
   * Rooms are lit by their own fittings; this only keeps shadows off the floor.
   */
  ambient: {
    sky: hex(0x3d5570),
    ground: hex(0x1d2026),
    intensity: 0.32,
    /** Directionless floor lift. Neutral so it cannot tint a sealed room. */
    fill: hex(0x2b2f36),
  },
  /** Ceiling fluorescent fixture. */
  fluoro: { color: EMIT.fluoro, intensity: 6.5, distance: 11, decay: 2 },
  /** Warm spill from inside a shop unit. */
  shop: { color: EMIT.shopWarm, intensity: 5.0, distance: 9, decay: 2 },
  /**
   * Sodium lamp on the car park.
   *
   * Reach matters more out here than anywhere else in the building, and it is
   * the one place where reach is free. A twelve-metre south wall puts the whole
   * forecourt in the moon's shadow, so every photon on that tarmac comes from a
   * lamp the centre paid for — and with a fourteen-metre cutoff at five metres
   * of height a column lit an eight-metre disc and nothing else. Six columns
   * around a thirty-four metre apron then left the middle of it at 6/255, which
   * is not dark, it is off, and the frontier where the entrance downlights gave
   * out read as a hard line drawn across the ground.
   *
   * These are baked, so the cutoff costs a longer trace at build time and
   * nothing at all per frame. Wide enough that neighbouring columns overlap is
   * the correct setting and there is no reason to be shy about it.
   *
   * Decay 1.7 rather than a physical 2 for the same reason. Inverse square on a
   * five-metre column puts nine tenths of the light in the first four metres:
   * turning it up far enough to reach the middle of the apron blew the base of
   * every column into a white disc first. The exponent is the only knob that
   * buys reach without buying a hotspot, and none of this is a real photon
   * anyway — it is a lightmap standing in for a sodium lamp nobody will measure.
   */
  sodium: { color: hex(0xffc79a), intensity: 22.0, distance: 26, decay: 1.85 },
  /**
   * Sky, as distinct from moon.
   *
   * The moon is one directional light and the south shell is twelve metres tall,
   * so the entire car park sits in its shadow — correctly; that is where a car
   * park south of a building is at night. What the shell does NOT block is the
   * other half of the sky, and leaving that out is why the apron went the colour
   * of its lamps and nothing else. Thirty-four metres of uniform tan is not a
   * moonlit exterior, it is an interior that happens to be outdoors.
   *
   * So: a few very high, very wide, very cool sources over the forecourt with a
   * near-linear falloff, standing in for skylight. Baked, so the only cost is
   * trace time, and they give the tarmac a blue floor for the sodium to sit on
   * top of — which is the whole point, because a warm pool is only warm next to
   * something that is not.
   */
  skyglow: { color: hex(0x9fb8dc), intensity: 15.0, distance: 62, decay: 1.15 },
  /**
   * The pool a guard's torch throws.
   *
   * Not a spotlight, and not placed at the lamp. The visible shaft is additive
   * geometry on the figure — free, and it costs the shader no light slot — while
   * this point light is pushed a couple of metres down the beam so what it
   * actually lights is the floor and wall in front of the guard. A point light
   * in the guard's fist would light their own chest, which is the exact opposite
   * of what a torch does to the person carrying it.
   *
   * Weaker and shorter than the thief's flashlight on purpose: a guard's torch
   * is meant to be something you SEE from across the atrium, not something that
   * lights the atrium.
   */
  torch: { color: EMIT.torch, intensity: 13, distance: 12, decay: 2 },
  /** The thief's flashlight. Tight, harsh, and the main gameplay light. */
  flashlight: {
    color: hex(0xfff4e0),
    intensity: 28,
    distance: 34,
    angle: 0.42,
    penumbra: 0.55,
    decay: 1.6,
  },
} as const;

/** Post-processing grade. Crushed blacks with a blue lift, mild bloom, heavy vignette. */
export const GRADE = {
  /**
   * AgX rolls off hard, so we sit above 1.0 to keep concrete in readable
   * mid-tones. The reference sheet is dark but never crushed — you can always
   * read the wall you are hiding against.
   */
  exposure: 1.45,
  /**
   * Threshold has to clear the brightest NON-emitter in the game, which is
   * polished atrium tile under moonlight — at 0.88 it did not, and the entire
   * floor bloomed into a milky wash that swallowed the reflections, the shadows
   * and the grout lines. Emitters are authored well above this, so raising it
   * costs the signage nothing and buys back the whole floor.
   */
  bloom: { strength: 0.34, radius: 0.6, threshold: 0.97 },
  vignette: { amount: 0.62, softness: 0.45 },
  /** Per-frame animated grain. Low — we want film, not VHS. */
  grain: 0.035,
  /** Subtle lateral chromatic split at the frame edges. */
  aberration: 0.0018,
  /** Lift/gamma/gain style grade applied after tonemapping. */
  lift: [0.012, 0.018, 0.032] as const,
  gain: [1.0, 0.99, 0.97] as const,
  saturation: 0.86,
} as const;

/**
 * Shadow settings — one shadow-casting light, the moon.
 *
 * The frustum is PINNED to the building rather than dragged along behind the
 * player. Every shadow caster in the mall sits inside x ∈ [-33, 39] and
 * z ∈ [-68, 39], so a 112m box centred on `center` holds the lot with room to
 * spare — which means the depth map is a photograph of a building that never
 * moves, and it can be rendered once and kept. Following the camera cost 688
 * draw calls on every single frame to re-photograph the same walls from a point
 * six inches to the left. It also bought worse texels, not better: the box has
 * to be big enough for the whole mall either way, and pinning it lets the box
 * shrink from 140m to 112m.
 */
export const SHADOWS = {
  mapSize: 2048,
  cameraExtent: 56,
  /** Centre of the building's shadow-casting bulk, on the ground plane. */
  center: [3.2, -14.6] as const,
  bias: -0.0006,
  normalBias: 0.03,
} as const;
